// One paged, filterable, searchable list — the shape every console tab
// speaks (`{items, total, page, size, total_capped?}`).
//
// Lifted from the indexing tab: debounced search (400 ms), a monotonic
// request counter so a slow response from a superseded page/filter never
// overwrites fresher rows, and a `silent` reload for polling that must not
// flash the loading state. Filters live here too, so a chip, a select and
// the search box all reset the page the same way.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

export const PAGE_SIZES = [25, 50, 100, 200];

// Keys that shape the request but are not filters: picking a sort order must
// not light up "clear filters" or flip an empty table to "no match".
const ORDERING_KEYS = new Set(['sort', 'order']);

export function usePagedList(url, { size: initialSize = 50, params: extraParams = {}, enabled = true } = {}) {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(initialSize);
  const [filters, setFiltersState] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], total: 0, total_capped: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const seq = useRef(0);
  const extraRef = useRef(extraParams);
  extraRef.current = extraParams;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!url || !enabled) return null;
      const mine = ++seq.current;
      if (!silent) setLoading(true);
      const params = { page, size, ...extraRef.current };
      if (search) params.search = search;
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') params[key] = value;
      }
      const res = await api.get(url, { params });
      if (mine !== seq.current) return null;
      if (res.success) {
        const body = Array.isArray(res.response) ? { items: res.response, total: res.response.length } : res.response;
        setData({ total_capped: false, ...body });
        setError(null);
        setLoadedAt(new Date());
        // Landed past the end (a delete drained the page): clamp back.
        if (body.items?.length === 0 && page > 1) {
          setPage(Math.max(1, Math.ceil((body.total || 0) / size)));
        }
      } else {
        setError(res.message);
      }
      if (!silent) setLoading(false);
      return res;
    },
    [url, enabled, page, size, search, filters],
  );

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = useCallback((key, value) => {
    setFiltersState((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    setPage(1);
  }, []);
  const clearFilters = useCallback(() => {
    // Ordering survives a clear — it is a view preference, not a filter.
    setFiltersState((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => ORDERING_KEYS.has(k))));
    setSearchInput('');
    setSearch('');
    setPage(1);
  }, []);
  const changeSize = useCallback((next) => {
    setSize(next);
    setPage(1);
  }, []);

  const hasFilters = Boolean(search) || Object.entries(filters).some(([k, v]) => !ORDERING_KEYS.has(k) && v && v !== 'all');
  const pages = data.total_capped ? Infinity : Math.max(1, Math.ceil((data.total || 0) / size));

  return {
    items: data.items || [],
    total: data.total || 0,
    totalCapped: Boolean(data.total_capped),
    extra: data,
    page,
    pages,
    size,
    setPage,
    setSize: changeSize,
    filters,
    setFilter,
    clearFilters,
    hasFilters,
    searchInput,
    setSearchInput,
    search,
    loading,
    error,
    loadedAt,
    reload: load,
  };
}
